import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Volume2, VolumeX, ShieldCheck, ExternalLink } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { VideoTile } from "@/components/VideoTile";
import { getJSON, resolveMedia } from "@/lib/api";
import { videoSource } from "@/lib/youtube";
import { Seo } from "@/components/Seo";

const Label = ({ children, dark }) => (
  <span className={`text-xs font-semibold uppercase tracking-[0.16em] ${dark ? "text-[var(--ember)]" : "text-[var(--ember)]"}`}>
    {children}
  </span>
);

const CountUp = ({ value }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  useEffect(() => {
    const m = String(value).match(/([^\d.-]*)([\d.]+)(.*)/);
    if (!m) {
      setDisplay(value);
      return;
    }
    const [, pre, num, suf] = m;
    const target = parseFloat(num);
    const decimals = (num.split(".")[1] || "").length;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setDisplay(value);
      return;
    }
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const dur = 1200;
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min((t - t0) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        const cur = (target * eased).toFixed(decimals);
        setDisplay(`${pre}${cur}${suf}`);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    if (el) io.observe(el);
    return () => io.disconnect();
  }, [value]);
  return <span ref={ref}>{display}</span>;
};

const MetaItem = ({ label, children }) =>
  children ? (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{label}</p>
      <p className="mt-1.5 text-[15px] font-medium text-[var(--ink)]">{children}</p>
    </div>
  ) : null;

export default function CreativeProject() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/gallery");
  };
  const vidRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const { data: project, isLoading, isError } = useQuery({
    queryKey: ["project", slug],
    queryFn: () => getJSON(`/projects/${slug}`),
    retry: false,
  });
  const { data: all = [] } = useQuery({
    queryKey: ["projects", "All"],
    queryFn: () => getJSON("/projects"),
  });

  useEffect(() => {
    vidRef.current?.play().catch(() => {});
  }, [slug, project]);

  if (isLoading) {
    return <div className="flex min-h-[70svh] items-center justify-center text-[var(--muted)]">Loading…</div>;
  }
  if (isError || !project) {
    return (
      <section className="mx-auto flex min-h-[70svh] max-w-3xl flex-col items-start justify-center px-6 md:px-12">
        <p className="text-2xl font-semibold tracking-tight">Piece not found.</p>
        <Link to="/gallery" className="mt-4 font-semibold text-[var(--ember)]">← Back to the gallery</Link>
      </section>
    );
  }

  const idx = all.findIndex((p) => p.slug === project.slug);
  const prev = idx > 0 ? all[idx - 1] : null;
  const next = idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null;
  const related = all
    .filter((p) => p.slug !== project.slug && (p.vertical === project.vertical || p.category === project.category))
    .slice(0, 3);
  const gallery = (project.gallery || []).map(resolveMedia);

  const vid = videoSource(project.youtube_url);
  const isEmbed = vid.kind === "youtube" || vid.kind === "vimeo";
  const embedSrc =
    vid.kind === "youtube"
      ? `https://www.youtube.com/embed/${vid.id}?autoplay=1&mute=1&loop=1&playlist=${vid.id}&controls=0&showinfo=0&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1&disablekb=1`
      : vid.kind === "vimeo"
        ? `https://player.vimeo.com/video/${vid.id}?autoplay=1&muted=1&loop=1&background=1&controls=0`
        : null;
  const heroMp4 = vid.kind === "mp4" ? vid.src : resolveMedia(project.video_src);

  const toggleMute = () => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <>
      <Seo
        title={`${project.title} — ${project.client || "Case Study"}`}
        description={(project.overview || project.blurb || `${project.title}: ${(project.tags && project.tags[0]) || project.category || "creative"} work by The AI Dept.`).slice(0, 160)}
        path={`/creative/${project.slug}`}
        type="article"
        image={resolveMedia(project.poster) || undefined}
      />
      {/* Full-bleed video hero */}
      <section className="relative flex min-h-[100svh] w-full overflow-hidden" data-testid="project-hero">
        <div className="absolute inset-0">
          {isEmbed ? (
            <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid={`project-hero-${vid.kind}`}>
              <iframe
                title={project.title}
                className="absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2"
                src={embedSrc}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          ) : (
            <video
              ref={vidRef}
              className="h-full w-full object-cover"
              src={heroMp4 || undefined}
              poster={resolveMedia(project.poster) || undefined}
              muted={muted}
              loop
              autoPlay
              playsInline
              preload="metadata"
              aria-hidden={muted ? "true" : undefined}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#141210] via-[#141210]/45 to-[#141210]/40" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col justify-between px-6 pb-16 pt-28 md:px-12 md:pb-20">
          <button type="button" onClick={goBack} className="inline-flex w-fit items-center gap-2 text-sm font-medium text-white/80 transition-colors hover:text-white" data-testid="project-back">
            <ArrowLeft size={16} /> Back to the gallery
          </button>
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--ember)]">
              {(project.tags && project.tags.length ? project.tags : [project.type || project.project_type, project.category].filter(Boolean)).join(" · ")}
            </p>
            <h1 className="mt-6 t-hero text-white">
              {project.title}
            </h1>
            <p className="mt-4 text-base text-white/75 sm:text-lg">
              {project.client} · {project.vertical} · {project.date}
            </p>
          </div>
        </div>

        {!isEmbed && (
          <button onClick={toggleMute} className="absolute bottom-8 right-6 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/30 bg-black/30 text-white backdrop-blur-sm transition-colors hover:border-[var(--ember)] hover:text-[var(--ember)] md:right-12" aria-label={muted ? "Unmute video" : "Mute video"} data-testid="project-sound">
            {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
        )}
      </section>

      {/* Meta grid */}
      <section className="border-b border-black/5 bg-[var(--bg-base)] py-16" data-testid="project-meta">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <MetaItem label="Client">{project.client}</MetaItem>
            <MetaItem label="Released">{project.date}</MetaItem>
            <MetaItem label="Timeframe">{project.timeframe}</MetaItem>
            <MetaItem label="Project type">{project.project_type}</MetaItem>
          </div>
          {(project.services?.length > 0 || project.live_url) && (
            <div className="mt-10 flex flex-wrap items-center gap-3">
              {project.services?.map((s) => (
                <span key={s} className="rounded-full border border-black/10 bg-[var(--bg-contrast)] px-4 py-2 text-sm font-medium text-[var(--ink)]/70">
                  {s}
                </span>
              ))}
              {project.live_url && (
                <a href={project.live_url} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex items-center gap-2 rounded-full bg-[var(--mint)] px-5 py-2.5 text-sm font-semibold text-[var(--charcoal)] transition-colors hover:bg-[var(--ember-deep)]">
                  Visit live site <ExternalLink size={15} />
                </a>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Overview */}
      {project.overview && (
        <section className="bg-[var(--bg-base)] py-24 md:py-32" data-testid="project-overview">
          <div className="mx-auto max-w-5xl px-6 md:px-12">
            <Reveal><Label>Overview</Label></Reveal>
            <Reveal delay={80}>
              <p className="mt-6 text-2xl font-semibold leading-snug tracking-[-0.02em] sm:text-3xl">
                {project.overview}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {gallery[0] && (
        <Reveal className="mx-auto max-w-7xl px-6 md:px-12">
          <img src={gallery[0]} alt={`${project.title} — still`} className="aspect-video w-full rounded-2xl object-cover" />
        </Reveal>
      )}

      {/* Challenge */}
      {project.challenge && (
        <section className="bg-[var(--bg-base)] py-24 md:py-32" data-testid="project-challenge">
          <div className="mx-auto max-w-5xl px-6 md:px-12">
            <Reveal><Label>The challenge</Label></Reveal>
            <Reveal delay={80}>
              <p className="mt-6 text-xl leading-relaxed text-[var(--ink)] sm:text-2xl">
                {project.challenge}
              </p>
            </Reveal>
          </div>
          {(gallery[1] || gallery[2]) && (
            <div className="mx-auto mt-14 grid max-w-7xl gap-6 px-6 md:grid-cols-2 md:px-12">
              {[gallery[1], gallery[2]].filter(Boolean).map((g, i) => (
                <Reveal key={i} delay={i * 80}>
                  <img src={g} alt={`${project.title} — still ${i + 2}`} className="aspect-[4/3] w-full rounded-2xl object-cover" />
                </Reveal>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Solution */}
      {project.solution && (
        <section className="bg-[var(--bg-contrast)] py-24 md:py-32" data-testid="project-solution">
          <div className="mx-auto max-w-5xl px-6 md:px-12">
            <Reveal><Label>The solution</Label></Reveal>
            <Reveal delay={80}>
              <p className="mt-6 text-xl leading-relaxed text-[var(--ink)] sm:text-2xl">
                {project.solution}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* Results + metrics */}
      {(project.results || project.metrics?.length > 0) && (
        <section className="bg-[var(--dark)] py-24 text-[var(--dark-text)] md:py-32" data-testid="project-results">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Label dark>Results</Label>
            {project.results && (
              <Reveal delay={80}>
                <p className="mt-6 max-w-3xl text-2xl font-semibold leading-snug tracking-[-0.02em] sm:text-3xl">
                  {project.results}
                </p>
              </Reveal>
            )}
            {project.metrics?.length > 0 && (
              <div className="mt-14 grid gap-8 sm:grid-cols-3">
                {project.metrics.map((m, i) => (
                  <Reveal key={i} delay={i * 90}>
                    <div className="border-t border-white/15 pt-6">
                      <span className="text-5xl font-semibold tracking-[-0.03em] text-[var(--ember)] sm:text-6xl">
                        <CountUp value={m.value} />
                      </span>
                      <p className="mt-3 text-sm text-white/70">{m.label}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Final thoughts */}
      {project.final_thoughts && (
        <section className="bg-[var(--bg-base)] py-24 md:py-32" data-testid="project-final">
          <div className="mx-auto max-w-4xl px-6 md:px-12">
            <Reveal><Label>Final thoughts</Label></Reveal>
            <Reveal delay={80}>
              <p className="mt-6 text-2xl font-semibold leading-snug tracking-[-0.02em] sm:text-3xl">
                {project.final_thoughts}
              </p>
            </Reveal>
            <Reveal delay={140}>
              <div className="mt-8 inline-flex items-center gap-2 rounded-full bg-[var(--ember)]/10 px-4 py-2 text-[13px] font-semibold text-[var(--ember)]">
                <ShieldCheck size={15} /> Produced by the Growth Team · {project.agent} · cleared by AI Standards
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Credits */}
      {project.credits?.length > 0 && (
        <section className="bg-[var(--bg-contrast)] py-24 md:py-28" data-testid="project-credits">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Label>Credits</Label>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {project.credits.map((c, i) => (
                <Reveal key={i} delay={(i % 3) * 70}>
                  <div className="border-t border-black/10 pt-5">
                    <p className="text-lg font-semibold tracking-tight">{c.name}</p>
                    <p className="mt-1 text-sm text-[var(--muted)]">{c.role}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Prev / Next */}
      {(prev || next) && (
        <section className="bg-[var(--bg-base)] py-16" data-testid="project-prevnext">
          <div className="mx-auto grid max-w-7xl gap-4 px-6 sm:grid-cols-2 md:px-12">
            {prev ? (
              <Link to={`/creative/${prev.slug}`} className="group rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-6 transition-all hover:-translate-y-1 hover:border-[var(--ember)]/30">
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]"><ArrowLeft size={13} /> Previous</span>
                <p className="mt-2 text-lg font-semibold tracking-tight">{prev.title}</p>
              </Link>
            ) : <div />}
            {next ? (
              <Link to={`/creative/${next.slug}`} className="group rounded-2xl border border-black/5 bg-[var(--bg-contrast)] p-6 text-right transition-all hover:-translate-y-1 hover:border-[var(--ember)]/30">
                <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Next <ArrowRight size={13} /></span>
                <p className="mt-2 text-lg font-semibold tracking-tight">{next.title}</p>
              </Link>
            ) : <div />}
          </div>
        </section>
      )}

      {/* More projects */}
      {related.length > 0 && (
        <section className="bg-[var(--bg-contrast)] py-24 md:py-32" data-testid="project-related">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <Label>More from the studio</Label>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {related.map((p, i) => (
                <VideoTile key={p.slug} project={p} aspect="aspect-[4/5]" delay={i * 90} />
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBand />
    </>
  );
}
